import React from 'react';
import { FormattedMessage } from 'react-intl';
import { buildCompanyInsight } from '../../../../../utils/insights/buildCompanyInsight';
import { formatCoverageStartDate, getCompanyInsightCoverageStart } from '../utils/companyInsightCoverage';
import { formatCompanyInsightLocationList } from '../utils/companyInsightLocationList';
import { Msg } from '../../../../../i18n-review/client/Msg';

interface Props {
  companyId?: string | null;
  companyHiringTrendsByLocale?: {
    en?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
    fi?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
    sv?: { locationDistribution: Record<string, number>; languageRequirements: Record<string, number> };
  } | null;
  companyMonthlyCounts?: Record<string, number> | null;
  locale?: string;
}

export const CompanyInsightCard: React.FC<Props> = ({
  companyId,
  companyHiringTrendsByLocale,
  companyMonthlyCounts,
  locale = 'en',
}) => {
  const trends =
    companyHiringTrendsByLocale?.[locale as 'en' | 'fi' | 'sv'] ?? companyHiringTrendsByLocale?.en;

  const insight = buildCompanyInsight(companyId ?? undefined, trends);
  if (!insight) return null;

  const topLocations = insight.topLocations.filter((loc) => loc.name.trim() !== '');
  const locationList = formatCompanyInsightLocationList(
    topLocations.map((loc) => loc.name),
    locale,
  );
  const coverageStart = getCompanyInsightCoverageStart(companyMonthlyCounts);
  const coverageStartLabel = formatCoverageStartDate(coverageStart, locale) ?? 'our index start';

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-jc-page/90 border border-jc-border/80">
      <div className="flex-shrink-0 mt-0.5 w-5 h-5 text-jc-accent">
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1 1 1 0 010 2 1 1 0 01-1-1z" clipRule="evenodd" />
          <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-jc-ink-secondary leading-relaxed">
          <strong className="text-jc-ink">{companyId}</strong>{' '}
          {topLocations.length > 0 ? (
            <Msg
              id="companyInsight.bodyWithLocations"
              defaultMessage="appears in {count, plural, one {# indexed job posting} other {# indexed job postings}} in JobCrawls' Finland dataset since {sinceDate}. In that historical index, the strongest location signals for this employer are {locationList}."
              description="Fact-based company insight copy clarifying indexed historical coverage"
              values={{
                count: insight.totalPositions,
                sinceDate: coverageStartLabel,
                locationList,
              }}
            />
          ) : (
            <Msg
              id="companyInsight.bodyNoLocations"
              defaultMessage="appears in {count, plural, one {# indexed job posting} other {# indexed job postings}} in JobCrawls' Finland dataset since {sinceDate}. This reflects historical index data, not a live count of open vacancies."
              description="Fact-based company insight copy clarifying indexed historical coverage without location breakdown"
              values={{ count: insight.totalPositions, sinceDate: coverageStartLabel }}
            />
          )}
          {insight.englishPercent !== null ? (
            <>
              {' '}
              <Msg
                id="companyInsight.englishRequirement"
                defaultMessage="{percent}% of those indexed postings mention English as a requirement."
                description="English language requirement share for company listings on JobCrawls"
                values={{ percent: insight.englishPercent }}
              />
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
};
